import Header from "@components/layout/header";
import Footer from "@components/layout/footer";
import { loadTranslation } from "@lib/utils";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { Plural, Trans, t } from "@lingui/macro";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import GiveawayHero from "@public/subdomain/giveaway/giveaway-hero.webp";
import { generateSubdomainPath } from "@components/layout/shared/link-props";

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV == "production"
  );

  return { props: { translation } };
};

const GiveawayTNC = () => {
  const title = t`Giveaway 2023 Terms & Conditions - Lumi News`;
  const description = t`Invite friends and families to Lumi News and stand a chance to win big. PS5, mattress, neck massagers, and more!`;

  const router = useRouter();
  let { locale, defaultLocale } = router;
  const seoLocale = "/" + locale || defaultLocale;

  const seoImages = {
    en: "https://luminews.my/subdomain/giveaway/giveaway-en-seoimage.webp",
    ms: "https://luminews.my/subdomain/giveaway/giveaway-ms-seoimage.webp",
    zh: "https://luminews.my/subdomain/giveaway/giveaway-en-seoimage.webp",
  };

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          url: `https://luminews.my${
            seoLocale.includes("en") ? "" : seoLocale
          }${router.asPath}`,
          title: title,
          description: description,
          images: [
            {
              url: seoImages[locale],
              alt: title,
            },
          ],
        }}
      />

      <Header />

      <main className="bg-white">
        <div className="relative mx-auto h-[120px] max-w-4xl md:mt-8 md:h-[250px]">
          <Image
            src={GiveawayHero}
            alt="Giveaway Hero"
            layout="fill"
            objectFit="cover"
          />
        </div>
        <div className="mx-auto max-w-4xl py-8 px-4 md:px-0">
          <p className="font-poppins text-3xl font-bold text-primary md:text-center">
            <Trans>Lumi For The Win Giveaway: Terms and Conditions</Trans>
          </p>

          <p className="my-4 text-left lg:text-center">
            <span className="font-sen text-text-darkest">
              <Trans>Also available in</Trans>
            </span>{" "}
            <Link
              href={generateSubdomainPath("/giveaway")}
              locale={locale === "zh" || locale === "en" ? "ms" : "en"}
            >
              <span className="cursor-pointer font-sen font-bold text-primary underline">
                {locale === "zh" || locale === "en" ? "BM" : "Bahasa Inggeris"}.
              </span>
            </Link>
          </p>

          <div className="font-sen text-text-darkest md:text-center">
            <p className="my-4">
              <Trans>
                From now until 31 December 2023, invite your friends to the Lumi
                News app for a ahacne to win big.
              </Trans>
            </p>

            <p className="my-4">
              <Trans>
                You can win yourself a PS5, a Joey Mattress of your size choice,
                neck massagers and more!
              </Trans>
            </p>
          </div>

          <p className="my-4 mt-8 font-poppins text-xl">
            <strong>1. </strong>
            <strong>
              <Trans>Introduction</Trans>
            </strong>
          </p>
          <p className="font-sen text-text-dark">
            <Trans>
              The Lumi News For The Win (FTW) Giveaway (henceforth “the
              campaign”) is organised by Lumi Global Sdn Bhd (1407575-K) with
              promotional and advertising purposes.  Lumi News is a free{" "}
              <a
                href="https://bit.ly/luminewsgiveaway"
                target="_blank"
                rel="noreferrer"
                className="text-primary2 underline"
              >
                mobile news app
              </a>{" "}
              (
              <a
                href="https://luminews.my"
                target="_blank"
                rel="noreferrer"
                className="text-primary2 underline"
              >
                https://luminews.my
              </a>
              ) that aggregates news from over 80+ news sources. Lumi News is
              incorporated in Malaysia, and headquartered at WeWork, Mercu 2,
              Level 40, Jalan Bangsar KL Eco City, Bangsar, 59200 Kuala Lumpur,
              and their contact email address is hello@luminews.my.  Any
              questions or concerns regarding this campaign should be directed
              to Lumi News at the contact information provided.
            </Trans>
          </p>

          <p className="my-4 font-poppins text-xl">
            <strong>2. </strong>
            <strong>
              <Trans>Campaign Period</Trans>
            </strong>
          </p>
          <p className="font-sen text-text-dark">
            <Trans>
              The campaign will run from 22 November 2023 to 31 December 2023
              ("Campaign Period"). All entries received outside of this Campaign
              Period will be disqualified. Lumi News reserves the right to make
              changes to the campaign period as they may deem fit.
            </Trans>
          </p>

          <p className="my-4 font-poppins text-xl">
            <strong>3. </strong>
            <strong>
              <Trans>Eligibility & Entry</Trans>
            </strong>
          </p>
          <div className="font-sen text-text-dark">
            <p>
              <Trans>
                The campaign is subject to all applicable federal, state, and
                local laws and regulations. Participation in the campaign
                constitutes the participant's full and unconditional agreement
                to these terms and conditions. In addition, the participant
                agrees to accept any and all decisions made by Lumi News, as
                final and binding in anything related to the content. The
                campaign is subject to the following eligibility criteria:
              </Trans>
            </p>
            <ul className="ml-6 list-circle">
              <li>
                <Trans>
                  Campaign is open to Malaysian residents, over the age of 16
                  years old.
                </Trans>
              </li>
              <li>
                <Trans>
                  Campaign is only open to participants that download the Lumi
                  News app.
                </Trans>
              </li>
              <li>
                <Trans>
                  Participants must share their Lumi referral links to the
                  following referrals in order to qualify as a successful
                  referral
                </Trans>
              </li>
              <div className="ml-6">
                <li>
                  <Trans>
                    Referrals who do not have the Lumi News app downloaded prior
                    to campaign, or
                  </Trans>
                </li>
                <li>
                  <Trans>
                    Referrals who do not have the Lumi News app downloaded since
                    22 August 2023 or earlier.
                  </Trans>
                </li>
              </div>
              <li>
                <Trans>
                  Referrals must complete the following actions in order for
                  participants to qualify
                </Trans>
              </li>
              <div className="ml-6">
                <li>
                  <Trans>
                    Install the Lumi News app with your friend’s unique Lumi
                    link
                  </Trans>
                </li>
                <li>
                  <Trans>Sign up/Login via Google, Apple or Facebook, or</Trans>
                </li>
                <li>
                  <Trans>‘Explore the app’ as guest</Trans>
                </li>
              </div>
              <li>
                <Trans>
                  The personal data with which the participants use to sign in
                  to their Lumi News account must be true data.
                </Trans>
              </li>
              <li>
                <Trans>
                  Participants may share their Lumi referral links as many times
                  as they wish throughout the campaign period to increase
                  chances of winning.
                </Trans>
              </li>
              <li>
                <Trans>
                  Participants can check their referral count by visiting their
                  Profile on the Lumi News app &gt; FTW Giveaway banner.
                  Participants should check that they have updated the Lumi News
                  app to the latest version to view the information.
                </Trans>
                <ul className="ml-6 list-circle">
                  <li>
                    <Trans>
                      Participants should check that they have updated the Lumi
                      News app to the latest version to view the information.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Referral counts in the app are updated every hour between
                      6am-2am daily.
                    </Trans>
                  </li>
                </ul>
              </li>
              <li>
                <Trans>
                  Participants should enable push notifications for the Lumi
                  News app and not mute nor disable the settings on their mobile
                  device before the drawing of winners on 1 January 2024 to
                  receive the winner’s announcement from Lumi News.
                </Trans>
              </li>
            </ul>
            <p>
              <Trans>
                Employees of Lumi Global Sdn Bhd, its subsidiaries, and their
                immediate family members (spouse, parent, and child) and
                household members (whether related or not) are not eligible to
                participate in the campaign.
              </Trans>
            </p>
          </div>

          <p className="my-4 font-poppins text-xl">
            <strong>4. </strong>
            <strong>
              <Trans>Prizes, winner selection, and winner announcement</Trans>
            </strong>
          </p>
          <div className="font-sen text-text-dark">
            <p>
              <Trans>
                A total of 35 prizes will be awarded in this campaign:
              </Trans>
            </p>
            <ul className="mb-4 ml-6 list-circle">
              <li>
                <Trans>
                  1 (one) Sony Playstation PS5 - Disc Edition 825GB (inclusive
                  of 1 DualSense Wireless Controller);
                </Trans>
              </li>
              <li>
                <Trans>
                  1 (one) Joey Mattress of the winner’s choice - single, super
                  single, queen, or king size;
                </Trans>
              </li>
              <li>
                <Trans>3 (three) Philip Neck Massagers;</Trans>
              </li>
              <li>
                <Trans>10 (ten) RM100 ZUS Coffee vouchers, and</Trans>
              </li>
              <li>
                <Trans>20 (twenty) RM10 preloaded Touch 'n Go NFC cards.</Trans>
              </li>
            </ul>
            <p className="mb-4">
              <Trans>
                1 (one) winner can only be awarded with 1 (one) prize in this
                campaign.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                The winner for each prize will be selected based on the number
                of successful referrals. Eligibility for winner selection is as
                follows:
              </Trans>
            </p>
          </div>
          <table className="mb-4 w-full border-collapse border border-light-background">
            <thead className="text-left align-top font-sen font-bold text-text-darkest">
              <tr>
                <th className="border border-light-background p-2">Prize</th>
                <th className="border border-light-background p-2">
                  <Trans>Number of winners</Trans>
                </th>
                <th className="border border-light-background p-2">
                  <Trans>Eligibility to win</Trans>
                </th>
              </tr>
            </thead>
            <tbody className="align-top font-sen text-text-dark">
              <tr>
                <td className="border border-light-background p-2">
                  <Trans>
                    1 x Sony Playstation PS5 - Disc Edition 825GB (inclusive of
                    1 DualSense Wireless Controller)
                  </Trans>
                </td>
                <td className="border border-light-background p-2">
                  1{" "}
                  <Plural
                    value={1}
                    one={<Trans>winner</Trans>}
                    other={<Trans>winners</Trans>}
                  />
                </td>
                <td className="border border-light-background p-2">
                  <Trans>Minimum</Trans> 50 <Trans>successful referrals</Trans>
                </td>
              </tr>
              <tr>
                <td className="border border-light-background p-2">
                  <Trans>
                    1 x Joey Mattress of winner’s choice - single, super single,
                    queen, or king size
                  </Trans>
                </td>
                <td className="border border-light-background p-2">
                  1{" "}
                  <Plural
                    value={1}
                    one={<Trans>winner</Trans>}
                    other={<Trans>winners</Trans>}
                  />
                </td>
                <td className="border border-light-background p-2">
                  30 <Trans>successful referrals</Trans>
                </td>
              </tr>
              <tr>
                <td className="border border-light-background p-2">
                  1 x <Trans>Philip Neck Massager</Trans>
                </td>
                <td className="border border-light-background p-2">
                  3{" "}
                  <Plural
                    value={3}
                    one={<Trans>winner</Trans>}
                    other={<Trans>winners</Trans>}
                  />
                </td>
                <td className="border border-light-background p-2">
                  15 <Trans>successful referrals</Trans>
                </td>
              </tr>
              <tr>
                <td className="border border-light-background p-2">
                  <Trans>RM100 ZUS Coffee voucher</Trans>
                </td>
                <td className="border border-light-background p-2">
                  10{" "}
                  <Plural
                    value={10}
                    one={<Trans>winner</Trans>}
                    other={<Trans>winners</Trans>}
                  />
                </td>
                <td className="border border-light-background p-2">
                  10 <Trans>successful referrals</Trans>
                </td>
              </tr>
              <tr>
                <td className="border border-light-background p-2">
                  <Trans>RM10 preloaded Touch 'n Go NFC card</Trans>
                </td>
                <td className="border border-light-background p-2">
                  20{" "}
                  <Plural
                    value={20}
                    one={<Trans>winner</Trans>}
                    other={<Trans>winners</Trans>}
                  />
                </td>
                <td className="border border-light-background p-2">
                  5 <Trans>successful referrals</Trans>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="font-sen text-text-dark">
            <p className="mb-4">
              <Trans>
                Referrals are deemed successful based on the criteria outlined
                in Section 3 above.
              </Trans>
            </p>
            {/* <p>
              <Trans>
                Participants can check their referral count by visiting their
                Profile on the Lumi News app and tapping into the Invite
                friends/FTW Giveaway banner. Participants should check that they
                have updated the Lumi News app to the latest version to view the
                information.
              </Trans>
            </p> */}
            {/* <p className="mb-4">
              <Trans>
                The winners will be selected by random drawing on 1 January
                2024.
              </Trans>
            </p> */}
            <p>
              <Trans>
                The winner will be notified by a push notification on the Lumi
                News app within 7 days of the winner selection, by which they
                will be redirected to fill in a form with personal information
                for delivery arrangements.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                Important: Lumi News will send a push notification to each
                winner to inform them that they’ve won. To receive the push
                notification, participants must ensure that they have allowed
                push notifications for the Lumi News app before the drawing of
                winners on 1 January 2024 and have not muted nor disabled the
                settings on their mobile device. Steps to enable push
                notifications and view Notification History can be found on the{" "}
                <a
                  className="text-primary2 underline"
                  href="https://about.luminews.my/faq"
                  target="_blank"
                  rel="noreferrer"
                >
                  Lumi News FAQ
                </a>
                .
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                The winner is responsible for their own notification settings
                and communication surveillance. Should the announcement
                notification be overlooked by the winner, resulting in a failure
                to respond in 48 hours from the time it was sent, Lumi News
                deserves the right to disqualify the winner, and select another
                winner without prior notice.
              </Trans>
            </p>
            <p>
              <Trans>
                Upon being selected, winners must share personal details for
                shipping and delivery arrangements, including but not limited
                to:
              </Trans>
            </p>
            <ul className="my-4 ml-6 list-circle">
              <li>
                <Trans>
                  Full Name of recipient (in accordance with NRIC/Passport)
                </Trans>
              </li>
              <li>
                <Trans>
                  Email address of recipient (for Lumi News to share tracking
                  information)
                </Trans>
              </li>
              <li>
                <Trans>Mobile number of recipient</Trans>
              </li>
              <li>
                <Trans>
                  Delivery address of recipient (within Malaysia only)
                </Trans>
              </li>
            </ul>
            <p className="mb-4">
              <Trans>
                Tracking information for all prizes will be provided by Lumi
                News unless otherwise stated. Lumi News is not responsible for
                the damage or loss of items during delivery.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                In the event of a failed delivery, Lumi News will only attempt 1
                (one) re-delivery. Should the recipient be unreachable the
                second time, they must collect the prize themselves at the Lumi
                News office (WeWork, Mercu 2, Level 40, Jalan Bangsar KL Eco
                City, Bangsar, 59200 Kuala Lumpur, Malaysia) at an agreed upon
                time between Mondays to Wednesdays during office hours, except
                public holidays.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                Delivery of the Joey Mattress: The shipping and delivery of the
                Joey Mattress prize will be handled by the Joey Mattress team 
                (“Partner Organisation”). Once the qualifying winner has
                responded to the Lumi News email within the given time, the
                winner will be in direct contact with the Partner Organisation
                to communicate mattress size preference, personal details, and
                tracking information. Lumi News is not responsible for any
                communication disputes between the winner and the Partner
                Organisation.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                The prize is not transferable or exchangeable and cannot be
                substituted for cash. Lumi News reserves the right to substitute
                the prize with a prize of equal or greater value if the
                advertised prize becomes unavailable for any reason. All costs
                and expenses associated with the acceptance and use of the prize
                that are not expressly stated as part of the prize are the sole
                responsibility of the winner. Lumi News is not responsible for
                any warranty or guarantee on the prize, and the winner accepts
                the prize "as is" without any warranty or guarantee.
              </Trans>
            </p>
          </div>

          <p className="my-4 font-poppins text-xl">
            <strong>5. </strong>
            <strong>
              <Trans>Limitations & Disqualification</Trans>
            </strong>
          </p>
          <div className="font-sen text-text-dark">
            <p className="mb-4">
              <Trans>
                Lumi News reserves the right to disqualify any participant who
                violates these terms and conditions or who tampers with the
                entry process, including but not limited to fraud or cheating.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                The prize is not transferable and will not be subject to
                changes, alterations, or compensations. The prize cannot be
                redeemed for cash nor can it be changed for a different product.
                Lumi News reserves the right to carry out changes, to extend or
                to cancel the campaign for any justifiable reason with prior
                legal communication.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                It is the responsibility of the qualified winner to claim the
                prize by 1 February 2024 (“Claim Period”). Lumi News reserves
                the right not to entertain any claim received outside the Claim
                Period.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                Any tax or fiscal fee that may arise from the acceptance of the
                prize will be the responsibility of the winner, as well as any
                other expense derived from the campaign that has not been
                previously assumed by Lumi News in the Terms and Conditions.
              </Trans>
            </p>
          </div>

          <p className="my-4 font-poppins text-xl">
            <strong>6. </strong>
            <strong>
              <Trans>Publications of Comments & Opinions</Trans>
            </strong>
          </p>
          <p className="font-sen text-text-dark">
            <Trans>
              No comments or opinions whose content is considered inappropriate,
              offensive, insulting or discriminatory, or that could violate the
              rights of third parties, will be allowed. Nor will comments be
              permitted against any individual that are an infringement of
              personal honour, personal and family privacy, or personal image
              rights. We will not be responsible for any damages caused by
              comments made by Campaign Participants, or that may at any time
              offend the sensitivities of other Participants. Any attempt to
              hinder the proper development of the Contest, either by human
              intervention or by means of an automaton, will lead to the
              immediate disqualification of the participant, annulling their
              candidacy.
            </Trans>
          </p>

          <p className="my-4 font-poppins text-xl">
            <strong>7. </strong>
            <strong>
              <Trans>Discharge of Liability</Trans>
            </strong>
          </p>
          <div className="font-sen text-text-dark">
            <p className="mb-4">
              <Trans>
                By participating in the campaign, participants agree to release,
                discharge, and hold harmless Lumi News, its affiliates,
                subsidiaries, advertising and campaign agencies, and their
                respective officers, directors, employees, and agents from any
                and all liability, loss, damage, or injury arising out of or
                related to participation in the campaign or the acceptance, use,
                misuse, or possession of any prize received through the
                campaign. Lumi News makes no representations or warranties of
                any kind, express or implied, regarding the prize or the
                campaign. Lumi News shall not be liable for any direct,
                indirect, incidental, special, or consequential damages arising
                out of or in any way connected with the campaign, whether based
                on contract, tort, strict liability, or any other theory of
                liability, even if Lumi News has been advised of the possibility
                of such damages.
              </Trans>
            </p>
            <p className="mb-4">
              <Trans>
                Lumi News also reserves the right to cancel, suspend, or modify
                the campaign in any way, without notice, in the event of any
                cause beyond its control. Lumi News is not responsible for any
                late, incomplete, or misdirected entries, or for any error,
                omission, interruption, deletion, defect, delay in operation or
                transmission, communications line failure, theft or destruction,
                or unauthorised access to or alteration of entries. Lumi News is
                not responsible for any injury or damage to participants' or any
                other person's computer/mobile device related to or resulting
                from participating in the campaign or downloading materials from
                or use of the Lumi News app. Lumi News is not responsible for
                any technical or other problems that may arise in connection
                with the campaign, including but not limited to problems with
                the internet or with any telecommunications network.
              </Trans>
            </p>
          </div>

          <p className="my-4 font-poppins text-xl">
            <strong>8. </strong>
            <strong>
              <Trans>Data protection & publicity</Trans>
            </strong>
          </p>
          <div className="font-sen text-text-dark">
            <p>
              <Trans>
                The company responsible for processing your data is Lumi Global
                Sdn Bhd located in WeWork, Mercu 2, Level 40, Jalan Bangsar KL
                Eco City, Bangsar, 59200 Kuala Lumpur, Malaysia. The data
                provided in the contest will be treated and stored with the
                utmost respect and data security.
              </Trans>
            </p>
            <p>
              <Trans>
                By participating in the campaign, participants agree to allow
                Lumi News to process their personal data for the purposes of
                administering the campaign, including contacting participants
                and verifying their eligibility. Participants also agree to
                allow Lumi News to use their name, likeness, photograph, and any
                statements they make about the campaign in any promotional or
                advertising materials related to the campaign, without
                compensation or notice. However, Lumi News will only process
                personal data in accordance with applicable data protection
                laws, including obtaining any necessary consents and providing
                participants with any required notices or rights. Participants
                may have the right to request access to, rectification, or
                erasure of their personal data or to restrict or object to its
                processing. Any personal information collected from participants
                in the campaign will be used in accordance with Lumi News's{" "}
                <a
                  className="text-primary2 underline"
                  href="https://luminews.my/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                >
                  privacy policy
                </a>
                .
              </Trans>
            </p>
          </div>

          <p className="my-4 font-poppins text-xl">
            <strong>9. </strong>
            <strong>
              <Trans>Amendments</Trans>
            </strong>
          </p>
          <p className="font-sen text-text-dark">
            <Trans>
              We reserve the right to modify or extend these promotional bases,
              to the extent that it does not harm or impair the rights of the
              participants in the Campaign.
            </Trans>
          </p>

          <p className="my-4 font-poppins text-xl">
            <strong>10. </strong>
            <strong>
              <Trans>Agreement</Trans>
            </strong>
          </p>
          <p className="font-sen text-text-dark">
            <Trans>
              By participating in the campaign, participants agree to be bound
              by these terms and conditions and any decisions made by Lumi News.
            </Trans>
          </p>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default GiveawayTNC;
